import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';
import { Button } from './ui/button';

const languages = [
  { code: 'fr', name: 'Français', flag: 'fr' },
  { code: 'en', name: 'English', flag: 'gb' },
  { code: 'es', name: 'Español', flag: 'es' },
  { code: 'de', name: 'Deutsch', flag: 'de' },
  { code: 'nl', name: 'Nederlands', flag: 'nl' },
  { code: 'sv', name: 'Svenska', flag: 'se' },
  { code: 'ro', name: 'Română', flag: 'ro' },
];

const Flag = ({ code, size = 20 }) => (
  <img
    src={`https://hatscripts.github.io/circle-flags/flags/${code}.svg`}
    width={size}
    height={size}
    alt=""
    className="rounded-full inline-block flex-shrink-0"
    loading="eager"
    style={{ minWidth: size, minHeight: size }}
  />
);

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = languages.find(l => l.code === i18n.language || i18n.language?.startsWith(l.code)) || languages[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
        data-testid="language-selector"
      >
        <Flag code={currentLang.flag} size={20} />
        <span className="hidden sm:inline text-sm font-medium">{currentLang.code.toUpperCase()}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-zinc-200 py-2 z-50 animate-in fade-in slide-in-from-top-2">
            {languages.map((lang) => {
              const isActive = i18n.language === lang.code || i18n.language?.startsWith(lang.code);
              return (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-zinc-50 transition-colors ${
                    isActive ? 'bg-fuchsia-50 text-fuchsia-700' : 'text-zinc-700'
                  }`}
                  data-testid={`lang-${lang.code}`}
                >
                  <Flag code={lang.flag} size={22} />
                  <span className="flex-1 text-left">{lang.name}</span>
                  {isActive && (
                    <Check className="h-4 w-4 text-fuchsia-600" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
